import React, { useState, useEffect } from "react";
import {
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import Text from "src/components/Displays/Text";
import { ChevronDown, QuestionMark } from "src/assets";
import NewLichtblickLogo from "src/assets/icons/lichtblick-tariff-selection.svg";
import { useTranslation } from "react-i18next";
import {
  SESSION_KEYS,
  SITE_TECHNICAL_DATA,
  USER_PROFILE_DATA,
  BONUS,
} from "src/consts/session";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { WallboxTypes } from "src/pages/Wallbox";
import { SmartMeterTypes } from "src/pages/SmartMeter";
import { Tariff } from "src/models/clientsModel";
import CheckWhite from "src/assets/icons/checkWhite.svg";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "src/consts/routes";
import { BatterySetupTypes } from "src/pages/BatterySetup";
import { PvSetupTypes } from "src/pages/PvSetup";

interface CalculatedSavingsSummaryProps {
  showSavingsResult?: boolean;
  themeName?: string;
  tariffsData?: Tariff[];
  getLogo: () => { houseImg: string; cssProps: any };
  toggleIsOverlayOpenFlex?: () => void;
  openByDefault?: boolean;
}

const CalculatedSavingsSummary: React.FC<CalculatedSavingsSummaryProps> = ({
  tariffsData,
  showSavingsResult = false,
  themeName,
  getLogo,
  openByDefault = false,
  toggleIsOverlayOpenFlex,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [getSessionStorageItem] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );
  const [getUserSessionStorage] = useSessionStorage<USER_PROFILE_DATA>(
    SESSION_KEYS.USER_PROFILE_DATA,
  );

  const savingsData = tariffsData && tariffsData[0];

  const flexTariffDisplayName =
    (savingsData && savingsData.flexTariff?.tariffDisplayName) ?? "";

  const savingsYearly = savingsData && savingsData.savings?.savingsYearly;

  const flexWorkingPricePerKwh =
    (savingsData && savingsData.flexTariff?.workingPricePerKwh) ?? "";
  const flexSupplyPriceFixedElementPerKwh =
    (savingsData && savingsData.flexTariff?.supplyPriceFixedElementPerKwh) ??
    "";
  const flexSupplyPriceFlexElementPerKwh =
    (savingsData && savingsData.flexTariff?.supplyPriceFlexElementPerKwh) ?? "";

  const estimatedYearlyCostsRaw = savingsData?.flexTariff?.estimatedYearlyCosts;

  const estimatedYearlyCostsNumber: number | null =
    estimatedYearlyCostsRaw != null
      ? Number(String(estimatedYearlyCostsRaw).replace(",", "."))
      : null;

  const estimatedMonthlyCosts: string =
    estimatedYearlyCostsNumber !== null
      ? (estimatedYearlyCostsNumber / 12).toFixed(2).replace(".", ",")
      : "-- ; --";

  const wallbox =
    getSessionStorageItem()?.wallboxInstalled === WallboxTypes.JA
      ? t("page.wallbox.yes")
      : t("page.wallbox.no");

  const smartMeter =
    getSessionStorageItem()?.smartMeterInstalled === SmartMeterTypes.JA
      ? t("page.smartMeter.yes")
      : t("page.smartMeter.no");

  const batteryCapacity =
    getSessionStorageItem()?.batterySetup?.batteryCapacity ?? 0;

  const handleValueChangeClick = () => {
    setBonus(null);
    setTariffs([]);
    navigate(ROUTES.PV_SETUP);
  };

  const [, setTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);
  const [, setBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);

  const batterySetup =
    getSessionStorageItem()?.batterySetup?.batteryInstalled ===
    BatterySetupTypes.JA
      ? `${batteryCapacity} kWh`
      : t("page.batterySetup.no");

  const pvSetup =
    getSessionStorageItem()?.pvSetup?.pvInstalled === PvSetupTypes.JA
      ? `${getSessionStorageItem()?.pvSetup?.pvPowerMaxKwp} kWp`
      : t("page.batterySetup.no");

  useEffect(() => {
    if (openByDefault) {
      setIsExpanded(true);
    }
  }, [openByDefault]);

  return (
    <Stack>
      <Accordion
        elevation={0}
        disableGutters
        expanded={isExpanded}
        onChange={(_, expanded) => setIsExpanded(expanded)}
      >
        <AccordionSummary
          sx={{
            padding: 0,
            bgcolor: "#DAECFA",
            width: { xs: "100%", md: "479px" },
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
          }}
        >
          <Stack sx={{ width: "100%" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                bgcolor: "#C6FFB2",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
                px: 3,
                py: 2,
                border: "1px solid #009A22",
              }}
            >
              <Text
                sx={{
                  color: "#222222",
                  fontSize: { xs: "11px", sm: "14px" },
                  fontWeight: 400,
                }}
                text={t("page.calculatedSavingsSummary.potentialSavings")}
              />
              <Stack direction="row">
                <Stack gap={0.5} direction={"row"} alignItems={"center"}>
                  <Text
                    sx={{
                      color: "#009A22",
                      fontSize: { xs: "11px", sm: "14px" },
                      fontWeight: 700,
                    }}
                    text={
                      <>
                        {`${savingsYearly} ${t(
                          "page.calculatedSavingsSummary.year",
                        )}`}
                      </>
                    }
                  />
                  <Text
                    sx={{
                      color: "#009A22",
                      marginTop: -3,
                      marginLeft: -0.5,
                      fontSize: { xs: "8px", sm: "10px" },
                      fontFamily: "isonType",
                      fontWeight: 700,
                    }}
                    text={`1`}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Stack
              gap={0}
              sx={{
                width: "100%",
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
              }}
            >
              <Stack
                sx={{
                  backgroundColor: "#084465",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    backgroundColor: "#105A83",
                    px: 3,
                    py: 3,
                    borderRadius: 2,
                    width: "98%",
                    margin: "0 auto",
                    marginTop: "6px",
                  }}
                >
                  <Stack
                    direction="column"
                    alignItems="flex-start"
                    gap={{ xs: 1, sm: 2 }}
                  >
                    <Text
                      sx={{
                        color: "#FFFFFF",
                        fontSize: { xs: "14px", sm: "14px" },
                        fontWeight: 700,
                        lineHeight: "17px",
                      }}
                      text={flexTariffDisplayName}
                    />
                    <Box
                      component="img"
                      src={NewLichtblickLogo}
                      alt="Ökostrom Icon"
                      sx={{
                        height: { xs: 16, sm: 20 },
                        width: "auto",
                      }}
                    />
                  </Stack>

                  <Stack
                    direction="column"
                    alignItems="flex-end"
                    sx={{ color: "#FFFFFF" }}
                  >
                    <Stack direction="row">
                      <Stack gap={0} direction="row" alignItems="center">
                        <Text
                          sx={{
                            color: "#FFFFFF",
                            fontSize: "15px",
                            fontWeight: 700,
                          }}
                          text={<>{`${estimatedMonthlyCosts} €/mtl.`}</>}
                        />
                        <Text
                          sx={{
                            color: "#FFFFFF",
                            marginTop: -1,
                            fontSize: { xs: "10px", sm: "10px" },
                            fontFamily: "isonType",
                            fontWeight: 700,
                          }}
                          text={`1`}
                        />
                      </Stack>
                    </Stack>

                    <Text
                      sx={{ color: "#FFFFFF", fontSize: "10px", mt: -2 }}
                      text={t(
                        "page.tariff.tariffSelectionContainer.calculatedCosts",
                      )}
                    />
                  </Stack>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                  sx={{
                    py: 2,
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    backgroundColor: "#084465",
                  }}
                >
                  <Stack
                    direction={{ xs: "row", sm: "row" }}
                    spacing={1.5}
                    mt={2}
                    sx={{ padding: 0, px: { xs: 8, sm: 10 } }}
                    width="100%"
                    justifyContent="space-between"
                  >
                    {[
                      t("page.calculatedSavingsSummary.checkbox.point1"),
                      t("page.calculatedSavingsSummary.checkbox.point3"),
                    ].map((feature, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        alignItems="center"
                        spacing={{ xs: 1, sm: 2 }}
                      >
                        <Box
                          component="img"
                          src={CheckWhite}
                          sx={{
                            width: 10,
                            height: 10,
                          }}
                        />
                        <Text
                          text={feature}
                          sx={{
                            fontSize: { xs: "10px", sm: "12px" },
                            color: "#FFFFFF",
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </Stack>

              {!isExpanded && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    py: 3,
                  }}
                  onClick={() => setIsExpanded(true)}
                >
                  <Text
                    sx={{
                      color: "#000",
                      fontSize: "11px",
                      fontWeight: 400,
                      textDecoration: "underline",
                    }}
                    text={t("page.tariff.details")}
                  />
                  <ChevronDown />
                </Stack>
              )}
            </Stack>
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <Stack
            sx={{
              px: 3,
              py: 2,
              gap: 2,
              bgcolor: "#DAECFA",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
            }}
          >
            <Text
              sx={{ color: "#222", fontSize: "12px", fontWeight: 700 }}
              text={t("page.calculatedSavingsSummary.title1")}
            />
            <Divider />
            <Text
              sx={{
                color: "#222",
                fontSize: "11px",
                fontWeight: 400,
                lineHeight: 1.5,
              }}
              text={t("page.calculatedSavingsSummary.description")}
            />
            {toggleIsOverlayOpenFlex && (
              <QuestionMark
                onClick={toggleIsOverlayOpenFlex}
                style={{ marginLeft: 8, cursor: "pointer" }}
              />
            )}
            <Stack gap={0.5}>
              <Stack direction="row" justifyContent="space-between" gap={0}>
                <Text
                  sx={{ color: "#222", fontSize: "12px", fontWeight: 700 }}
                  text={t("page.calculatedSavingsSummary.title2")}
                />
                <Text
                  sx={{
                    color: "#0075CF",
                    fontSize: "12px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                  text={t("page.calculatedSavingsSummary.changeValues")}
                  onClick={handleValueChangeClick}
                />
              </Stack>
              <Divider />

              {[
                {
                  label: t(
                    "page.calculatedSavingsSummary.annualConsumptionLable",
                  ),
                  value: `${
                    getUserSessionStorage()?.yearlyGridSupplyKwh
                  } kWh/Jahr`,
                },
                {
                  label: t("page.address.formFields.zipCode.label"),
                  value: `${getSessionStorageItem()?.pvSetup?.postalCode}`,
                },
                {
                  label: t("page.calculatedSavingsSummary.pvLabel"),
                  value: `${pvSetup}`,
                },
                {
                  label: t("page.calculatedSavingsSummary.wallboxInstalled"),
                  value: `${wallbox}`,
                },
                {
                  label: t("page.calculatedSavingsSummary.homeStorage"),
                  value: `${batterySetup}`,
                },
                {
                  label: t("page.calculatedSavingsSummary.smartMeter"),
                  value: `${smartMeter}`,
                },
              ].map((item, idx) => (
                <Stack
                  key={idx}
                  direction="row"
                  justifyContent="space-between"
                  gap={0}
                >
                  <Text
                    sx={{ color: "#222", fontSize: "11px", fontWeight: 400 }}
                    text={`${item.label}:`}
                  />
                  <Text
                    sx={{ color: "#222", fontSize: "11px", fontWeight: 400 }}
                    text={item.value}
                  />
                </Stack>
              ))}
            </Stack>
            <Stack gap={0.5}>
              <Text
                sx={{ color: "#222", fontSize: "12px", fontWeight: 700 }}
                text={t("page.calculatedSavingsSummary.title3")}
              />
              <Divider />

              {[
                {
                  label: t("page.tariff.timeline.timelineItem3.list.listItem4"),
                  value: `${flexSupplyPriceFlexElementPerKwh} ct/kWh`,
                },
                {
                  label: t("page.tariff.timeline.timelineItem3.list.listItem3"),
                  value: `${flexSupplyPriceFixedElementPerKwh} ct/kWh`,
                },
                {
                  label: t("page.tariff.timeline.timelineItem3.list.listItem2"),
                  value: `${flexWorkingPricePerKwh} ct/kWh`,
                },
              ].map((item, idx) => (
                <Stack
                  key={idx}
                  direction="row"
                  justifyContent="space-between"
                  gap={0}
                >
                  <Text
                    sx={{ color: "#222", fontSize: "11px", fontWeight: 400 }}
                    text={`${item.label}:`}
                  />
                  <Text
                    sx={{ color: "#222", fontSize: "11px", fontWeight: 400 }}
                    text={item.value}
                  />
                </Stack>
              ))}
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{ cursor: "pointer", width: "100%", bgcolor: "#DAECFA" }}
              onClick={() => setIsExpanded(false)}
            >
              <Text
                sx={{
                  color: "#000",
                  fontSize: "11px",
                  fontWeight: 400,
                  textDecoration: "underline",
                }}
                text={t("page.calculatedSavingsSummary.closeSummary")}
              />
              <ChevronDown style={{ transform: "rotate(180deg)" }} />
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default CalculatedSavingsSummary;
